import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { InstallPrompt } from "@/components/InstallPrompt";
import { lazy, Suspense, useEffect } from "react";
import { registerServiceWorker } from "@/lib/registerServiceWorker";
import { useAnalyticsTracking } from "@/hooks/useAnalyticsTracking";

// Lazy load pages for code splitting to reduce initial bundle size
const Events = lazy(() => import("./pages/Events"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Admin = lazy(() => import("./pages/Admin"));
const OrganizerDashboard = lazy(() => import("./pages/OrganizerDashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const SuggestEvent = lazy(() => import("./pages/SuggestEvent"));
const Embed = lazy(() => import("./pages/Embed"));
const EmbedVertical = lazy(() => import("./pages/EmbedVertical"));
const Favorites = lazy(() => import("./pages/Favorites"));
const Documentation = lazy(() => import("./pages/Documentation"));
const Microblog = lazy(() => import("./pages/Microblog"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Install = lazy(() => import("./pages/Install"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const EventScanner = lazy(() => import("./pages/EventScanner"));
const CheckIn = lazy(() => import("./pages/CheckIn"));

const queryClient = new QueryClient();

// Analytics tracking wrapper component
const AnalyticsWrapper = ({ children }: { children: React.ReactNode }) => {
  useAnalyticsTracking();
  return <>{children}</>;
};

const App = () => {
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <AnalyticsWrapper>
                <Suspense fallback={
                  <div className="flex flex-col min-h-screen">
                    <div 
                      className="py-12 px-4 relative overflow-hidden"
                      style={{
                        backgroundImage: 'url(/hero-pattern.webp)',
                        backgroundSize: '400px',
                        backgroundRepeat: 'repeat',
                      }}
                    >
                      <div className="absolute inset-0 gradient-primary opacity-90" />
                      <div className="relative z-10 flex flex-col items-center justify-center text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-transparent border-t-white mb-4"></div>
                        <span className="text-white text-lg font-semibold">Loading events...</span>
                      </div>
                    </div>
                    <div className="flex-1 bg-background p-6">
                      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                          <div key={i} className="rounded-lg border bg-card overflow-hidden animate-pulse">
                            <div className="h-48 bg-muted" />
                            <div className="p-6 space-y-3">
                              <div className="h-4 bg-muted rounded w-3/4" />
                              <div className="h-4 bg-muted rounded w-1/2" />
                              <div className="h-4 bg-muted rounded w-2/3" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                }>
                  <main>
                    <Routes>
                      <Route path="/" element={<Events />} />
                      <Route path="/event/:eventId" element={<EventDetail />} />
                      <Route path="/e/:eventId" element={<EventDetail />} />
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/suggest" element={<SuggestEvent />} />
                      <Route path="/favorites" element={<Favorites />} />
                      <Route path="/embed" element={<Embed />} />
                      <Route path="/embed-vertical" element={<EmbedVertical />} />
                      <Route path="/documentation" element={<Documentation />} />
                      <Route path="/feed" element={<Microblog />} />
                      <Route path="/install" element={<Install />} />
                      <Route
                        path="/profile"
                        element={
                          <ProtectedRoute>
                            <Profile />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin"
                        element={
                          <ProtectedRoute requireAdmin>
                            <Admin />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/organizer-dashboard"
                        element={
                          <ProtectedRoute requireOrganizer>
                            <OrganizerDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/event-scanner"
                        element={
                          <ProtectedRoute requireAdmin>
                            <EventScanner />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/check-in/:eventId"
                        element={
                          <ProtectedRoute>
                            <CheckIn />
                          </ProtectedRoute>
                        }
                      />
                      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </main>
                  <InstallPrompt />
                </Suspense>
              </AnalyticsWrapper>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
